import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AlertDialog from '../components/AlertDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import { getApiUrl } from '../config/api.config';

const SettingsPage = () => {
    const navigate = useNavigate();
    const { tenantId } = useParams();
    const [settings, setSettings] = useState({});
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [showAddModal, setShowAddModal] = useState(false);
    const [newSetting, setNewSetting] = useState({ category: '', value: '', displayOrder: 1 });
    const [addingToCategory, setAddingToCategory] = useState(null);
    const [newOptionValue, setNewOptionValue] = useState('');
    const [alert, setAlert] = useState({ isOpen: false, title: '', message: '', type: 'info' });
    const [confirm, setConfirm] = useState({ isOpen: false, title: '', message: '', onConfirm: null, type: 'warning' });
    const [initializing, setInitializing] = useState(false);

    const categories = [
        { key: 'ORGANIZATION_TYPE', label: 'Organization Types', description: 'Types of investor organizations' },
        { key: 'STARTUP_STAGE', label: 'Startup Stages', description: 'Funding stages for startups' },
        { key: 'INDUSTRY', label: 'Industries', description: 'Industry sectors' },
        { key: 'EVENT_TYPE', label: 'Event Types', description: 'Types of pitch events' },
        { key: 'ENTITY_TYPE', label: 'Entity Types', description: 'Indian company entity types' },
        { key: 'INDIAN_STATE', label: 'Indian States', description: 'States and Union Territories' }
    ];

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        setLoading(true);
        try {
            const response = await fetch(getApiUrl('settings/grouped'), {
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            const data = await response.json();
            setSettings(data);
        } catch (error) {
            console.error('Failed to load settings:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInitialize = async () => {
        setInitializing(true);
        try {
            const response = await fetch(getApiUrl('settings/initialize'), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });

            if (response.ok) {
                setAlert({
                    isOpen: true,
                    title: 'Success',
                    message: 'Default settings initialized successfully! You can now use the dropdowns.',
                    type: 'success'
                });
                loadSettings();
            } else {
                throw new Error('Failed to initialize settings');
            }
        } catch (error) {
            console.error('Failed to initialize settings:', error);
            setAlert({
                isOpen: true,
                title: 'Error',
                message: 'Failed to initialize default settings. Please try again.',
                type: 'error'
            });
        } finally {
            setInitializing(false);
        }
    };



    const handleEdit = (setting) => {
        setEditingId(setting.id);
        setEditValue(setting.value);
    };

    const handleSave = async (id) => {
        try {
            const setting = Object.values(settings).flat().find(s => s.id === id);
            await fetch(getApiUrl(`settings/${id}`), {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({ ...setting, value: editValue })
            });
            setEditingId(null);
            loadSettings();
        } catch (error) {
            console.error('Failed to update setting:', error);
        }
    };

    const handleDelete = (id) => {
        setConfirm({
            isOpen: true,
            title: 'Delete Setting',
            message: 'Are you sure you want to delete this setting?',
            type: 'danger',
            onConfirm: async () => {
                await performDelete(id);
            }
        });
    };

    const performDelete = async (id) => {
        try {
            await fetch(getApiUrl(`settings/${id}`), {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
            });
            loadSettings();
            setAlert({ isOpen: true, title: 'Success', message: 'Setting deleted successfully', type: 'success' });
        } catch (error) {
            console.error('Failed to delete setting:', error);
            setAlert({ isOpen: true, title: 'Error', message: 'Failed to delete setting', type: 'error' });
        }
    };

    const handleAdd = async () => {
        try {
            await fetch(getApiUrl('settings'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify(newSetting)
            });
            setShowAddModal(false);
            setNewSetting({ category: '', value: '', displayOrder: 1 });
            loadSettings();
        } catch (error) {
            console.error('Failed to add setting:', error);
        }
    };

    const handleAddToCategory = async (categoryKey) => {
        if (!newOptionValue.trim()) return;

        try {
            // Get the highest display order for this category
            const categorySettings = settings[categoryKey] || [];
            const maxOrder = categorySettings.length > 0
                ? Math.max(...categorySettings.map(s => s.displayOrder || 0))
                : 0;

            await fetch(getApiUrl('settings'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                },
                body: JSON.stringify({
                    category: categoryKey,
                    value: newOptionValue,
                    displayOrder: maxOrder + 1
                })
            });
            setAddingToCategory(null);
            setNewOptionValue('');
            loadSettings();
            setAlert({ isOpen: true, title: 'Success', message: 'Option added successfully', type: 'success' });
        } catch (error) {
            console.error('Failed to add option:', error);
            setAlert({ isOpen: true, title: 'Error', message: 'Failed to add option', type: 'error' });
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-2xl font-semibold text-gray-900">Settings</h2>
                    <p className="text-sm text-gray-500 mt-1">Manage picklist values and system configurations</p>
                </div>
                <div className="flex space-x-3">
                    <button
                        onClick={handleInitialize}
                        disabled={initializing}
                        className={`px-4 py-2 ${initializing ? 'bg-green-400' : 'bg-green-600 hover:bg-green-700'} text-white rounded-lg transition flex items-center space-x-2`}
                    >
                        {initializing ? (
                            <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        ) : (
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                        )}
                        <span>{initializing ? 'Initializing...' : 'Initialize Defaults'}</span>
                    </button>
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center space-x-2"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                        </svg>
                        <span>Add Setting</span>
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="">

                {/* Categories */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {categories.map((category) => (
                        <div key={category.key} className="bg-white rounded-lg shadow">
                            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-900">{category.label}</h2>
                                    <p className="text-sm text-gray-500">{category.description}</p>
                                </div>
                                <button
                                    onClick={() => {
                                        setAddingToCategory(category.key);
                                        setNewOptionValue('');
                                    }}
                                    className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition flex items-center space-x-1"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                                    </svg>
                                    <span>Add Option</span>
                                </button>
                            </div>
                            <div className="p-6">
                                {/* Add Option Input (shown when adding) */}
                                {addingToCategory === category.key && (
                                    <div className="mb-4 p-3 bg-blue-50 rounded-lg border-2 border-blue-200">
                                        <div className="flex items-center space-x-2">
                                            <input
                                                type="text"
                                                value={newOptionValue}
                                                onChange={(e) => setNewOptionValue(e.target.value)}
                                                onKeyPress={(e) => {
                                                    if (e.key === 'Enter') {
                                                        handleAddToCategory(category.key);
                                                    }
                                                }}
                                                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                placeholder="Enter new option..."
                                                autoFocus
                                            />
                                            <button
                                                onClick={() => handleAddToCategory(category.key)}
                                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center space-x-1"
                                                disabled={!newOptionValue.trim()}
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                </svg>
                                                <span>Add</span>
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setAddingToCategory(null);
                                                    setNewOptionValue('');
                                                }}
                                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* Existing Options */}
                                {settings[category.key] && settings[category.key].length > 0 ? (
                                    <div className="space-y-2">
                                        {settings[category.key].map((setting) => (
                                            <div key={setting.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
                                                {editingId === setting.id ? (
                                                    <input
                                                        type="text"
                                                        value={editValue}
                                                        onChange={(e) => setEditValue(e.target.value)}
                                                        className="flex-1 px-3 py-1 border border-gray-300 rounded"
                                                        autoFocus
                                                    />
                                                ) : (
                                                    <span className="text-gray-900">{setting.value}</span>
                                                )}
                                                <div className="flex space-x-2 ml-4">
                                                    {editingId === setting.id ? (
                                                        <>
                                                            <button
                                                                onClick={() => handleSave(setting.id)}
                                                                className="text-green-600 hover:text-green-800"
                                                            >
                                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                                                                </svg>
                                                            </button>
                                                            <button
                                                                onClick={() => setEditingId(null)}
                                                                className="text-gray-600 hover:text-gray-800"
                                                            >
                                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                                                </svg>
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button
                                                                onClick={() => handleEdit(setting)}
                                                                className="text-blue-600 hover:text-blue-800"
                                                            >
                                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                                </svg>
                                                            </button>
                                                            <button
                                                                onClick={() => handleDelete(setting.id)}
                                                                className="text-red-600 hover:text-red-800"
                                                            >
                                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                </svg>
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-gray-500 text-center py-4">No options yet. Click "Add Option" to add values.</p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Add Modal */}
            {showAddModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Setting</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                                <select
                                    value={newSetting.category}
                                    onChange={(e) => setNewSetting({ ...newSetting, category: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                >
                                    <option value="">Select Category</option>
                                    {categories.map((cat) => (
                                        <option key={cat.key} value={cat.key}>{cat.label}</option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
                                <input
                                    type="text"
                                    value={newSetting.value}
                                    onChange={(e) => setNewSetting({ ...newSetting, value: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                    placeholder="Enter value"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                                <input
                                    type="number"
                                    value={newSetting.displayOrder}
                                    onChange={(e) => setNewSetting({ ...newSetting, displayOrder: parseInt(e.target.value) })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                onClick={() => setShowAddModal(false)}
                                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAdd}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                Add
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Alert Dialog */}
            <AlertDialog
                isOpen={alert.isOpen}
                onClose={() => setAlert({ ...alert, isOpen: false })}
                title={alert.title}
                message={alert.message}
                type={alert.type}
            />

            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={confirm.isOpen}
                onClose={() => setConfirm({ ...confirm, isOpen: false })}
                onConfirm={confirm.onConfirm || (() => { })}
                title={confirm.title}
                message={confirm.message}
                type={confirm.type}
            />
        </div>
    );
};

export default SettingsPage;
